<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Vue
import { computed, ref } from "vue"
import { useId } from "vue"

// Store
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"

// Type
import { BuildingSource } from "@types"
import { storeToRefs } from "pinia"
import { CONSENT_REQUEST_STATUS, CONSENT_REQUEST_TYPE } from "@core/constants"
import type { ConsentSentModalPayload } from "@core/components/source/dialog/consent-request-send-dialog.types"
import { resolveConsentRequestSendInvitation } from "@core/components/source/dialog/consent-request-send.helper"
import { formatIdSource } from "@core/helpers"

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    meterId: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// COMPOSABLES & STORES
// ================================

const buildingStore = useBuildingStore()
const sourceStore = useSourceStore()

const { building } = storeToRefs(buildingStore)
const { getSourceById } = storeToRefs(sourceStore)

const componentId = useId()

const showConsentSentModal = ref(false)
const consentSentModalPayload = ref<ConsentSentModalPayload | null>(null)

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Computed pour récupérer les infos de la source
 */
const getSource = computed(() => {
    const sourceInBuilding = building.value?.sources.find(
        (source: BuildingSource) => source._id_source === props.meterId
    ) as BuildingSource

    if (sourceInBuilding) return sourceInBuilding

    // Sinon on cherche dans le store source
    return getSourceById.value(props.meterId)
})

/**
 * Computed pour récupérer le type de la demande de consentement
 */
const getConsentRequestType = computed(() => {
    return getSource.value?.consentrequest || null
})

/**
 * Computed pour extraire toutes les données nécessaires pour les composants enfants
 */
const getConsentRequestData = computed(() => {
    const consentRequest = getSource.value?.consentrequest
    if (!consentRequest) {
        return {
            state: "",
            type: "",
            createdAt: "",
            requestId: "",
        }
    }

    const isMultiRequest = !!consentRequest._id_consentrequestmulti
    const trackingMails = isMultiRequest
        ? consentRequest._id_consentrequestmulti?.tracking?.mails || []
        : consentRequest.tracking?.mails || []

    // Récupère signedAt depuis tracking.states si disponible
    const signedAt =
        consentRequest.tracking?.states?.find(
            (s: { previous_state: string; date: string }) =>
                s.previous_state === CONSENT_REQUEST_STATUS.CREATED
        )?.date ||
        (isMultiRequest
            ? consentRequest._id_consentrequestmulti?.tracking?.states?.find(
                  (s: { previous_state: string; date: string }) =>
                      s.previous_state === CONSENT_REQUEST_STATUS.CREATED
              )?.date
            : undefined)

    return {
        state: consentRequest.state || "",
        type: consentRequest.type || "",
        createdAt: consentRequest.createdAt || "",
        signedAt,
        url: consentRequest.url,
        requestId: consentRequest._id || "",
        isMultiRequest,
        trackingMails: trackingMails.map(
            (m: { type: string; date: string }) => ({
                type: m.type,
                date: m.date,
            })
        ),
        trackingStates: isMultiRequest
            ? consentRequest._id_consentrequestmulti?.tracking?.states?.map(
                  (s: { previous_state: string; date: string }) => ({
                      previous_state: s.previous_state,
                      date: s.date,
                  })
              ) || []
            : [],
        signataireEmail:
            getSource.value?.consentement?.signataire?.user?.username,
        userToEmail: consentRequest.user_to?.username,
        sourceIdtype: getSource.value?.idtype,
        ddaList: getSource.value?.consentement?.droits_acces?.map(
            (dda: { date_creation: string; etat_droit_acces: string }) => ({
                date_creation: dda.date_creation,
                etat_droit_acces: dda.etat_droit_acces,
            })
        ),
        consentRequestMulti: {
            requestId: consentRequest._id_consentrequestmulti?._id || "",
            url: consentRequest._id_consentrequestmulti?.url || "",
            type: "SIGN",
            state: consentRequest._id_consentrequestmulti?.state || "",
        },
    }
})

/**
 * Libellé métier (idsource) pour la modale de suppression, distinct de l’`_id_source` Mongo.
 */
const getConsentDeleteSourceLabel = computed(() => {
    const s = getSource.value
    if (!s?.idsource) return undefined
    return formatIdSource(s.idsource, s.idtype ?? "")
})

// ================================
// INTERACTIONS UTILISATEUR
// ================================
/**
 * Callback pour envoyer ou relancer une invitation
 * @param config - Configuration pour l'envoi de l'invitation
 */
function onSendInvitation(config: { sendBack: boolean }): void {
    const data = getConsentRequestData.value
    const reqType = getConsentRequestType.value?.type
    const { dialogPayload } = resolveConsentRequestSendInvitation(
        {
            isMultiRequest: !!data.isMultiRequest,
            requestId: data.requestId,
            url: data.url,
            type: data.type,
            sourceId: props.meterId,
            consentRequestMulti: data.consentRequestMulti ?? {
                requestId: "",
                url: "",
                type: "",
            },
        },
        reqType,
        config
    )
    consentSentModalPayload.value = dialogPayload
    showConsentSentModal.value = true
}

function onConsentSentModalDone(): void {
    showConsentSentModal.value = false
    consentSentModalPayload.value = null
}

/**
 * Après envoi depuis la modale : recharge la source pour mettre à jour le consentement.
 */
async function onConsentInvitationSent(): Promise<void> {
    try {
        await sourceStore.fetchSourceById({ _id_source: props.meterId })
        const fresh = getSourceById.value(props.meterId)
        const b = building.value
        if (b?.sources?.length && fresh) {
            const idx = b.sources.findIndex(
                (s: BuildingSource) => s._id_source === props.meterId
            )
            if (idx !== -1) {
                b.sources.splice(idx, 1, {
                    ...b.sources[idx],
                    ...fresh,
                } as BuildingSource)
            }
        }
    } catch (error) {
        console.error(
            "Erreur lors du rafraîchissement de la source après relance :",
            error
        )
    }
}
</script>

<template>
    <div class="meter_card_invitation flex flex-col items-center gap-6">
        <MeterCardInvitationShare
            v-if="getConsentRequestType?.type === CONSENT_REQUEST_TYPE.SHARE"
            :state="getConsentRequestData.state"
            :type="getConsentRequestData.type"
            :created-at="getConsentRequestData.createdAt"
            :url="getConsentRequestData.url"
            :request-id="getConsentRequestData.requestId"
            :is-multi-request="getConsentRequestData.isMultiRequest"
            :tracking-mails="getConsentRequestData.trackingMails"
            :user-to-email="getConsentRequestData.userToEmail"
            :signataire-email="getConsentRequestData.signataireEmail"
            :source-id="props.meterId"
            :source-label="getConsentDeleteSourceLabel"
            :on-send-invitation="onSendInvitation"
            disable-delete
        />
        <MeterCardInvitationSign
            v-if="
                getConsentRequestType?.type === CONSENT_REQUEST_TYPE.SIGN ||
                getConsentRequestType?.type === CONSENT_REQUEST_TYPE.EXTEND ||
                getConsentRequestType?.type ===
                    CONSENT_REQUEST_TYPE.CHANGE_OWNER
            "
            :state="getConsentRequestData.state"
            :type="getConsentRequestData.type"
            :created-at="getConsentRequestData.createdAt"
            :signed-at="getConsentRequestData.signedAt"
            :url="getConsentRequestData.url"
            :request-id="getConsentRequestData.requestId"
            :is-multi-request="getConsentRequestData.isMultiRequest"
            :tracking-mails="getConsentRequestData.trackingMails"
            :tracking-states="getConsentRequestData.trackingStates"
            :signataire-email="getConsentRequestData.signataireEmail"
            :source-idtype="getConsentRequestData.sourceIdtype"
            :source-id="props.meterId"
            :source-label="getConsentDeleteSourceLabel"
            :dda-list="getConsentRequestData.ddaList"
            :on-send-invitation="onSendInvitation"
        />
        <BaseConfirmDialog :group="componentId" />
        <ConsentRequestSendDialog
            v-model:visible="showConsentSentModal"
            :payload="consentSentModalPayload"
            @sent="onConsentInvitationSent"
            @hide="onConsentSentModalDone"
        />
    </div>
</template>

<style scoped></style>
